import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { CartProvider } from './lib/cart';
import { TableOrderProvider } from './lib/table-context';
import { KitchenPauseProvider } from './lib/kitchen-pause-context';
import { KitchenPausedBanner } from './components/restaurant/KitchenPausedBanner';
import { getSubdomainSlug } from './lib/subdomain';
// Customer Pages
import { HomePage } from './pages/HomePage';
import { RestaurantListingPage } from './pages/RestaurantListingPage';
import { RestaurantPage } from './pages/RestaurantPage';
import { MenuPage } from './pages/MenuPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { OrderConfirmationPage } from './pages/OrderConfirmationPage';
import { TrackingPage } from './pages/TrackingPage';
import { TablePage } from './pages/TablePage';
// Admin Pages
import { LoginPage } from './pages/admin/LoginPage';
import { NoizaMetricsPage } from './pages/admin/NoizaMetricsPage';
import { DashboardPage } from './pages/admin/DashboardPage';
import { KDSPage } from './pages/admin/KDSPage';
import { InventoryPage } from './pages/admin/InventoryPage';
import { MetricsPage } from './pages/admin/MetricsPage';
import { MenuManagementPage } from './pages/admin/MenuManagementPage';
import { ImportPage } from './pages/admin/ImportPage';
import { StationSelectPage } from './pages/admin/StationSelectPage';
import { CajaOverviewPage } from './pages/admin/CajaOverviewPage';
import { TableManagementPage } from './pages/admin/TableManagementPage';
import { TableQRPage } from './pages/admin/TableQRPage';
import { PublicMenuPage } from './pages/PublicMenuPage';
// Tenant Access
import { TenantProvider } from './lib/tenant-context';
/** Redirects /admin/:slug → /admin/:slug/operacion (station select) */
function AdminRedirect() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  return <Navigate to={`/admin/${slug}/operacion`} replace data-id="element-0" />;
}
/**
 * Subdomain-aware admin redirect.
 * When on a tenant subdomain, /admin → /admin/dashboard
 */
function SubdomainAdminRedirect({
  slug
}: {
  slug: string;
}) {
  return <Navigate to="/admin/operacion" replace data-id="element-1" />;
}
/**
 * Wraps TenantProvider to inject the subdomain slug
 * when there's no :slug in the path params.
 */
function SubdomainTenantProvider({
  children,
  slug
}: {
  children: React.ReactNode;
  slug: string;
}) {
  // We pass the slug through a context-compatible wrapper
  // TenantProvider reads from useParams, but we need to override it.
  // Instead, we'll use the updated TenantProvider that accepts an optional slug prop.
  return <TenantProvider subdomainSlug={slug} data-id="element-2">{children}</TenantProvider>;
}
/**
 * Main routing component.
 * Detects subdomain and renders appropriate routes.
 */
function AppRoutes() {
  const subdomainSlug = getSubdomainSlug();
  // ─── SUBDOMAIN MODE ───
  // When accessed via {slug}.noiza.estrategiasdigitalesaz.com
  if (subdomainSlug) {
    return <Routes data-id="element-3">
        {/* Public restaurant page at root */}
        <Route path="/" element={<PublicMenuPage subdomainSlug={subdomainSlug} data-id="element-5" />} data-id="element-4" />

        {/* Cart & checkout still work */}
        <Route path="/cart" element={<CartPage data-id="element-7" />} data-id="element-6" />
        <Route path="/checkout" element={<CheckoutPage data-id="element-9" />} data-id="element-8" />
        <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-11" />} data-id="element-10" />
        <Route path="/track/:trackingToken" element={<TrackingPage data-id="element-13" />} data-id="element-12" />

        {/* Table ordering */}
        <Route path="/table/:tableNumber" element={<TablePage data-id="element-15" />} data-id="element-14" />
        <Route path="/mesa/:tableNumber/:token" element={<TablePage data-id="element-17" />} data-id="element-16" />

        {/* Admin routes — no :slug needed in path, resolved from subdomain */}
        <Route path="/admin/login" element={<LoginPage subdomainSlug={subdomainSlug} data-id="element-19" />} data-id="element-18" />
        <Route path="/admin" element={<SubdomainAdminRedirect slug={subdomainSlug} data-id="element-21" />} data-id="element-20" />
        <Route path="/admin/operacion" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-23">
              <StationSelectPage data-id="element-24" />
            </SubdomainTenantProvider>} data-id="element-22" />
        <Route path="/admin/overview" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-26">
              <CajaOverviewPage data-id="element-27" />
            </SubdomainTenantProvider>} data-id="element-25" />
        <Route path="/admin/dashboard" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-29">
              <DashboardPage data-id="element-30" />
            </SubdomainTenantProvider>} data-id="element-28" />
        <Route path="/admin/caja" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-32">
              <CajaOverviewPage data-id="element-33" />
            </SubdomainTenantProvider>} data-id="element-31" />
        <Route path="/admin/mesas" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-35">
              <TableManagementPage data-id="element-36" />
            </SubdomainTenantProvider>} data-id="element-34" />
        <Route path="/admin/mesas/qr" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-38">
              <TableQRPage data-id="element-39" />
            </SubdomainTenantProvider>} data-id="element-37" />
        <Route path="/admin/kds" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-41">
              <KDSPage data-id="element-42" />
            </SubdomainTenantProvider>} data-id="element-40" />
        <Route path="/admin/menu" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-44">
              <MenuManagementPage data-id="element-45" />
            </SubdomainTenantProvider>} data-id="element-43" />
        <Route path="/admin/inventory" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-47">
              <InventoryPage data-id="element-48" />
            </SubdomainTenantProvider>} data-id="element-46" />
        <Route path="/admin/metrics" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-50">
              <MetricsPage data-id="element-51" />
            </SubdomainTenantProvider>} data-id="element-49" />

        {/* Add import route for subdomain mode */}
        <Route path="/admin/import" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-53">
              <ImportPage data-id="element-54" />
            </SubdomainTenantProvider>} data-id="element-52" />

        {/* NOIZA Platform Metrics (global, not tenant-specific) */}
        <Route path="/admin/noiza/metrics" element={<NoizaMetricsPage data-id="element-56" />} data-id="element-55" />

        {/* Fallback: also support legacy path-based admin on subdomain */}
        <Route path="/admin/:slug/login" element={<LoginPage data-id="element-58" />} data-id="element-57" />
        <Route path="/admin/:slug" element={<AdminRedirect data-id="element-60" />} data-id="element-59" />
        <Route path="/admin/:slug/operacion" element={<TenantProvider data-id="element-62">
              <StationSelectPage data-id="element-63" />
            </TenantProvider>} data-id="element-61" />
        <Route path="/admin/:slug/dashboard" element={<TenantProvider data-id="element-65">
              <DashboardPage data-id="element-66" />
            </TenantProvider>} data-id="element-64" />
        <Route path="/admin/:slug/caja" element={<TenantProvider data-id="element-68">
              <CajaOverviewPage data-id="element-69" />
            </TenantProvider>} data-id="element-67" />
        <Route path="/admin/:slug/mesas" element={<TenantProvider data-id="element-71">
              <TableManagementPage data-id="element-72" />
            </TenantProvider>} data-id="element-70" />
        <Route path="/admin/:slug/mesas/qr" element={<TenantProvider data-id="element-74">
              <TableQRPage data-id="element-75" />
            </TenantProvider>} data-id="element-73" />
        <Route path="/admin/:slug/kds" element={<TenantProvider data-id="element-77">
              <KDSPage data-id="element-78" />
            </TenantProvider>} data-id="element-76" />
        <Route path="/admin/:slug/menu" element={<TenantProvider data-id="element-80">
              <MenuManagementPage data-id="element-81" />
            </TenantProvider>} data-id="element-79" />
        <Route path="/admin/:slug/inventory" element={<TenantProvider data-id="element-83">
              <InventoryPage data-id="element-84" />
            </TenantProvider>} data-id="element-82" />
        <Route path="/admin/:slug/metrics" element={<TenantProvider data-id="element-86">
              <MetricsPage data-id="element-87" />
            </TenantProvider>} data-id="element-85" />

        {/* Add legacy import route on subdomain */}
        <Route path="/admin/:slug/import" element={<TenantProvider data-id="element-89">
              <ImportPage data-id="element-90" />
            </TenantProvider>} data-id="element-88" />

        {/* Catch-all: any other path on subdomain → public menu */}
        <Route path="*" element={<PublicMenuPage subdomainSlug={subdomainSlug} data-id="element-92" />} data-id="element-91" />
      </Routes>;
  }
  // ─── STANDARD PATH-BASED MODE ───
  // When accessed via noiza.estrategiasdigitalesaz.com/:slug
  return <Routes data-id="element-93">
      {/* Customer Facing */}
      <Route path="/" element={<HomePage data-id="element-95" />} data-id="element-94" />
      <Route path="/restaurants" element={<RestaurantListingPage data-id="element-97" />} data-id="element-96" />
      <Route path="/r/:slug" element={<RestaurantPage data-id="element-99" />} data-id="element-98" />
      <Route path="/r/:slug/menu" element={<MenuPage data-id="element-101" />} data-id="element-100" />
      <Route path="/r/:slug/table/:tableNumber" element={<TablePage data-id="element-103" />} data-id="element-102" />
      <Route path="/r/:slug/mesa/:tableNumber/:token" element={<TablePage data-id="element-105" />} data-id="element-104" />
      <Route path="/cart" element={<CartPage data-id="element-107" />} data-id="element-106" />
      <Route path="/checkout" element={<CheckoutPage data-id="element-109" />} data-id="element-108" />
      <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-111" />} data-id="element-110" />
      <Route path="/track/:trackingToken" element={<TrackingPage data-id="element-113" />} data-id="element-112" />

      {/* NOIZA Platform Metrics (global, not tenant-specific) */}
      <Route path="/admin/noiza/metrics" element={<NoizaMetricsPage data-id="element-115" />} data-id="element-114" />

      {/* Admin - Login */}
      <Route path="/admin/:slug/login" element={<LoginPage data-id="element-117" />} data-id="element-116" />

      {/* Admin - Protected by TenantProvider */}
      <Route path="/admin/:slug" element={<AdminRedirect data-id="element-119" />} data-id="element-118" />
      <Route path="/admin/:slug/operacion" element={<TenantProvider data-id="element-121">
            <StationSelectPage data-id="element-122" />
          </TenantProvider>} data-id="element-120" />
      <Route path="/admin/:slug/dashboard" element={<TenantProvider data-id="element-124">
            <DashboardPage data-id="element-125" />
          </TenantProvider>} data-id="element-123" />
      <Route path="/admin/:slug/caja" element={<TenantProvider data-id="element-127">
            <CajaOverviewPage data-id="element-128" />
          </TenantProvider>} data-id="element-126" />
      <Route path="/admin/:slug/mesas" element={<TenantProvider data-id="element-130">
            <TableManagementPage data-id="element-131" />
          </TenantProvider>} data-id="element-129" />
      <Route path="/admin/:slug/mesas/qr" element={<TenantProvider data-id="element-133">
            <TableQRPage data-id="element-134" />
          </TenantProvider>} data-id="element-132" />
      <Route path="/admin/:slug/kds" element={<TenantProvider data-id="element-136">
            <KDSPage data-id="element-137" />
          </TenantProvider>} data-id="element-135" />
      <Route path="/admin/:slug/menu" element={<TenantProvider data-id="element-139">
            <MenuManagementPage data-id="element-140" />
          </TenantProvider>} data-id="element-138" />
      <Route path="/admin/:slug/inventory" element={<TenantProvider data-id="element-142">
            <InventoryPage data-id="element-143" />
          </TenantProvider>} data-id="element-141" />
      <Route path="/admin/:slug/metrics" element={<TenantProvider data-id="element-145">
            <MetricsPage data-id="element-146" />
          </TenantProvider>} data-id="element-144" />

      {/* Add import route for path-based mode */}
      <Route path="/admin/:slug/import" element={<TenantProvider data-id="element-148">
            <ImportPage data-id="element-149" />
          </TenantProvider>} data-id="element-147" />

      {/* Public Restaurant Page (Must be last) */}
      <Route path="/:slug" element={<RestaurantPage data-id="element-151" />} data-id="element-150" />
    </Routes>;
}
export function App() {
  return <CartProvider data-id="element-152">
      <TableOrderProvider data-id="element-153">
        <KitchenPauseProvider data-id="element-154">
          <BrowserRouter data-id="element-155">
            <KitchenPausedBanner data-id="element-156" />
            <div className="min-h-screen flex flex-col bg-brand-offwhite font-sans text-gray-900" data-id="element-157">
              <AppRoutes data-id="element-158" />
            </div>
          </BrowserRouter>
        </KitchenPauseProvider>
      </TableOrderProvider>
    </CartProvider>;
}